/**
 * Parses short_summary text from the agent into structured blocks with optional headers.
 * Supports common formats the agent may return:
 * - Markdown: ## Header or ### Header
 * - Bold: **Header**
 * - Colon-style: Header:
 * - Plain paragraphs (double newline)
 */

/**
 * Normalizes line breaks in short_summary before storing to DB.
 * Ensures \r\n -> \n and literal \\n (double-escaped) -> actual newline.
 */
export function preserveSummaryLineBreaks(text: string | null | undefined): string {
    if (text == null || typeof text !== 'string') return '';
    return text
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\\n/g, '\n');
}
export interface ShortSummaryBlock {
    title?: string;
    content: string;
}

const MARKDOWN_HEADER = /^#{1,6}\s+(.+)$/;
const BOLD_HEADER = /^\*\*(.+?)\*\*\s*$/;
const COLON_HEADER = /^(.+):\s*$/; // Line ending with colon
const MAX_HEADER_LENGTH = 80; // Lines longer than this are unlikely to be headers

export function parseShortSummaryIntoBlocks(text: string): ShortSummaryBlock[] {
    if (!text || typeof text !== 'string') return [];

    const blocks: ShortSummaryBlock[] = [];
    const rawBlocks = text.split(/\n\n+/).map((b) => b.trim()).filter(Boolean);

    for (const block of rawBlocks) {
        const lines = block.split('\n');
        const firstLine = lines[0]?.trim() || '';
        const restLines = lines.slice(1);

        let title: string | undefined;
        let content: string;

        // Markdown header: ## Title or ### Title
        const mdMatch = firstLine.match(MARKDOWN_HEADER);
        if (mdMatch) {
            title = mdMatch[1].trim();
            content = restLines.join('\n').trim();
        }
        // Bold header: **Title**
        else if (firstLine.match(BOLD_HEADER)) {
            const boldMatch = firstLine.match(BOLD_HEADER);
            title = boldMatch?.[1]?.trim();
            content = restLines.join('\n').trim();
        }
        // Colon-style: Title: (short line, likely a header)
        else if (
            firstLine.length <= MAX_HEADER_LENGTH &&
            firstLine.match(COLON_HEADER) &&
            (restLines.length > 0 || firstLine.length < 50)
        ) {
            const colonMatch = firstLine.match(COLON_HEADER);
            title = colonMatch?.[1]?.trim();
            content = restLines.join('\n').trim();
        }
        // No header - treat entire block as content
        else {
            content = block;
        }

        if (content || title) {
            blocks.push({ title, content });
        }
    }

    // If no structure detected, return single block with full text
    if (blocks.length === 0 && text.trim()) {
        blocks.push({ content: text.trim() });
    }

    return blocks;
}
